import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from "recharts";
import { Wallet, ExternalLink } from "lucide-react";
import { formatNumber, dec } from "@/lib/formatters";
import { tooltipStyle } from "./oversiktConstants";
import { DelKnapp } from "./DelKnapp";

const kr = (v) => (v == null ? "—" : `${formatNumber(Math.round(v))} kr`);
const n1 = (v) => (v == null ? "—" : dec(v, 1));

const Rangering = ({ rang, formatter }) => {
  if (!rang) return null;
  const foran = rang.rank - 1;
  return (
    <p className="text-xs text-[#8AA4BF] mt-1" data-testid="livsopphald-rangering">
      {rang.hoeg_er_verst ? (
        <>
          Nr. <span className="font-semibold text-[#103766]">{rang.rank}</span> av {rang.count} i landet – {foran} kommunar ligg høgare (høgast:{" "}
          {rang.verst.namn} {formatter(rang.verst.verdi)})
        </>
      ) : (
        <>
          <span className="font-semibold text-[#103766]">{foran}</span> av {rang.count} kommunar betaler ut mindre per månad (lågast:{" "}
          {rang.verst.namn} {formatter(rang.verst.verdi)})
        </>
      )}
    </p>
  );
};

export const LivsopphaldSection = ({ data, kommuneCode }) => {
  if (!data?.latest) return null;
  const { latest, satsar, history, gruppe_history, land_history, gruppe_namn, fylke_name, rangering } = data;
  const snittNamn = gruppe_namn ? "Storbyane" : fylke_name || "Fylket";
  const chart = history.map((r) => ({
    year: r.year,
    Kommunen: r.per_maanad,
    [snittNamn]: gruppe_history?.find((g) => g.year === r.year)?.per_maanad ?? null,
    Landet: land_history?.find((g) => g.year === r.year)?.per_maanad ?? null,
  }));
  const underSifo = latest.gap_sifo != null && latest.gap_sifo < 0;

  return (
    <Card className="bg-white border border-[#E6E9F0] rounded-lg mb-8" data-testid="livsopphald-card" id="livsopphald">
      <CardHeader className="border-b border-[#E6E9F0] pb-4">
        <div className="flex items-start justify-between gap-3">
          <CardTitle className="text-lg font-medium text-[#1F2A36] flex items-center gap-2">
            <Wallet className="w-5 h-5 text-[#103766]" />
            Livsopphald og sosialhjelp {latest.year}
          </CardTitle>
          {kommuneCode && <DelKnapp kommuneCode={kommuneCode} kommuneNamn={data.kommune_name} tema="ulikskap" testId="del-livsopphald" />}
        </div>
        <p className="text-sm text-[#4B647F]">
          Kva kommunen faktisk betaler ut i sosialhjelp per månad, målt mot SIFOs referansebudsjett for ein einsleg vaksen og den statlege
          rettleiande satsen for livsopphald.
        </p>
      </CardHeader>
      <CardContent className="pt-6 space-y-6">
        <div className="grid sm:grid-cols-3 gap-4">
          <div className="p-4 bg-[#F7F9FC] rounded-xl" data-testid="livsopphald-utbetaling">
            <p className="text-xs text-[#8AA4BF]">Utbetalt per stønadsmånad</p>
            <p className="text-2xl font-light text-[#1F2A36]">{kr(latest.per_maanad)}</p>
            <p className="text-xs text-[#4B647F] mt-1">
              {snittNamn} {kr(gruppe_history?.[gruppe_history.length - 1]?.per_maanad)} · Landet{" "}
              {kr(land_history?.[land_history.length - 1]?.per_maanad)}
            </p>
            <Rangering rang={rangering?.per_maanad} formatter={kr} />
          </div>
          <div className={`p-4 rounded-xl ${underSifo ? "bg-[#FBEAE7]" : "bg-[#F7F9FC]"}`} data-testid="livsopphald-sifo">
            <p className="text-xs text-[#8AA4BF]">Mot SIFOs referansebudsjett ({kr(satsar.sifo_einsleg)}/md)</p>
            <p className={`text-2xl font-light ${underSifo ? "text-[#C0392B]" : "text-[#3F8A86]"}`}>
              {latest.gap_sifo == null ? "—" : `${latest.gap_sifo > 0 ? "+" : "−"}${formatNumber(Math.abs(latest.gap_sifo))} kr`}
            </p>
            <p className="text-xs text-[#4B647F] mt-1">
              Utbetalinga er {n1(latest.pct_av_sifo)} % av referansebudsjettet
              {underSifo ? " – under det SIFO reknar som eit rimeleg forbruksnivå" : ""}
            </p>
          </div>
          <div className="p-4 bg-[#F7F9FC] rounded-xl" data-testid="livsopphald-statleg">
            <p className="text-xs text-[#8AA4BF]">Mot statleg rettleiande sats ({kr(satsar.statleg_einsleg)}/md)</p>
            <p className="text-2xl font-light text-[#1F2A36]">
              {latest.gap_statleg == null ? "—" : `${latest.gap_statleg > 0 ? "+" : "−"}${formatNumber(Math.abs(latest.gap_statleg))} kr`}
            </p>
            <p className="text-xs text-[#4B647F] mt-1">{n1(latest.pct_av_statleg)} % av den statlege satsen</p>
          </div>
        </div>

        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-2">
            Utbetalt per stønadsmånad mot SIFO og statleg sats
          </p>
          <div className="h-[280px]" data-testid="livsopphald-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chart} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
                <XAxis dataKey="year" stroke="#8AA4BF" fontSize={11} />
                <YAxis stroke="#8AA4BF" fontSize={11} width={62} tickFormatter={(v) => formatNumber(v)} />
                <Tooltip contentStyle={tooltipStyle} formatter={(v) => kr(v)} />
                <Legend wrapperStyle={{ fontSize: 11 }} iconSize={9} />
                <ReferenceLine y={satsar.sifo_einsleg} stroke="#C0392B" strokeDasharray="5 4"
                  label={{ value: `SIFO ${formatNumber(satsar.sifo_einsleg)}`, position: "insideTopRight", fontSize: 10, fill: "#C0392B" }} />
                <ReferenceLine y={satsar.statleg_einsleg} stroke="#B5832B" strokeDasharray="5 4"
                  label={{ value: `Statleg sats ${formatNumber(satsar.statleg_einsleg)}`, position: "insideBottomRight", fontSize: 10, fill: "#B5832B" }} />
                <Line type="monotone" dataKey="Kommunen" stroke="#103766" strokeWidth={2.5} dot={{ r: 3 }} connectNulls />
                <Line type="monotone" dataKey={snittNamn} stroke="#3F8A86" strokeWidth={2} dot={{ r: 2 }} connectNulls />
                <Line type="monotone" dataKey="Landet" stroke="#9CA3AF" strokeWidth={2} strokeDasharray="2 3" dot={false} connectNulls />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="livsopphald-detaljar">
          {[
            ["Sosialhjelpstilfelle", formatNumber(latest.tilfelle)],
            ["Del av befolkninga", latest.andel_av_folket == null ? "—" : `${n1(latest.andel_av_folket)} %`],
            ["Barn i familiar med sosialhjelp", formatNumber(latest.barn)],
            ["Gjennomsnittleg stønadstid", latest.stonadstid == null ? "—" : `${n1(latest.stonadstid)} md`],
            ["Med sosialhjelp 6 md eller meir", formatNumber(latest.langtid_6mnd)],
            ["Stønadstid når sosialhjelp er hovudinntekt", latest.stonadstid_hovudinntekt == null ? "—" : `${n1(latest.stonadstid_hovudinntekt)} md`],
            ["Samla stønadssum", latest.stonadssum == null ? "—" : `${formatNumber(Math.round(latest.stonadssum / 1000))} tusen kr`],
            ["Fekk økonomisk rådgjeving", formatNumber(latest.radgjeving)],
          ].map(([label, verdi]) => (
            <div key={label} className="p-3 bg-[#F7F9FC] rounded-lg">
              <p className="text-[11px] text-[#8AA4BF] leading-tight">{label}</p>
              <p className="text-sm text-[#1F2A36] tabular-nums mt-0.5">{verdi}</p>
            </div>
          ))}
        </div>

        <p className="text-xs text-[#8AA4BF]">
          Utbetalingstala er gjennomsnittleg utbetalt sosialhjelp per stønadsmånad frå SSB{" "}
          <a href="https://www.ssb.no/statbank/table/13995" target="_blank" rel="noopener noreferrer" className="underline inline-flex items-center gap-1">
            13995 <ExternalLink className="w-3 h-3" />
          </a>{" "}
          og{" "}
          <a href="https://www.ssb.no/statbank/table/14019" target="_blank" rel="noopener noreferrer" className="underline inline-flex items-center gap-1">
            14019 <ExternalLink className="w-3 h-3" />
          </a>
          . Utbetalinga dekkjer både livsopphald og husleige, straum og andre utgifter, mens SIFO-budsjettet ({satsar.sifo_kjelde}) og den statlege
          satsen ({satsar.statleg_kjelde}) berre dekkjer dagleg livsopphald – så samanlikninga viser nivået, ikkje heile biletet. Kor mange som lever
          under SIFO-budsjettet finst ikkje som offisiell statistikk per kommune; tala over er dei næraste som finst. Snittet for {snittNamn} og
          landet er vekta med talet på sosialhjelpstilfelle.
        </p>
      </CardContent>
    </Card>
  );
};

export default LivsopphaldSection;
